import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import { Dialog } from '@headlessui/react';
import {
  GiftIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  SparklesIcon,
  ChartBarIcon,
  NoSymbolIcon,
  EyeIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const REWARD_AMOUNT = 250;

function StatCard({ icon: Icon, label, value, color = 'blue', subtext }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  };

  return (
    <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-sm text-surface-500 dark:text-surface-400">{label}</p>
          <p className="text-2xl font-bold text-surface-900 dark:text-white">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </p>
          {subtext && (
            <p className="text-xs text-surface-400 dark:text-surface-500">{subtext}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ReferralDetailModal({ referral, isOpen, onClose }) {
  if (!referral) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-surface-200 dark:border-surface-700">
            <Dialog.Title className="text-xl font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <GiftIcon className="w-6 h-6 text-primary-500" />
              Referral Details
            </Dialog.Title>
          </div>
          
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className="text-surface-500 dark:text-surface-400">Status</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                referral.status === 'completed'
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                  : referral.status === 'blocked'
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
              }`}>
                {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
              </span>
            </div>

            {/* Referrer Company */}
            <div className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4">
              <h3 className="font-medium text-surface-900 dark:text-white mb-2">Referrer</h3>
              <p className="text-surface-700 dark:text-surface-300">{referral.referrerCompanyId?.name}</p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Code: {referral.referrerCompanyId?.referralCode}
              </p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Plan: {referral.referrerCompanyId?.billingPlan}
              </p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                First Payment: {referral.referrerCompanyId?.hasMadeFirstPayment ? 'Yes' : 'No'}
              </p>
            </div>

            {/* Referred Company */}
            <div className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4">
              <h3 className="font-medium text-surface-900 dark:text-white mb-2">Referred</h3>
              <p className="text-surface-700 dark:text-surface-300">{referral.referredCompanyId?.name}</p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Plan: {referral.referredCompanyId?.billingPlan}
              </p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                First Payment: {referral.referredCompanyId?.hasMadeFirstPayment ? 'Yes' : 'No'}
              </p>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Reward Received: {referral.referredCompanyId?.hasReceivedReferralReward ? 'Yes' : 'No'}
              </p>
            </div>

            {/* Rewards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4 text-center">
                <p className="text-sm text-surface-500 dark:text-surface-400">Referrer Reward</p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {referral.referrerRewardAmount || REWARD_AMOUNT}
                </p>
                <p className="text-xs text-surface-400">credits</p>
              </div>
              <div className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4 text-center">
                <p className="text-sm text-surface-500 dark:text-surface-400">Referred Reward</p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {referral.referredRewardAmount || REWARD_AMOUNT}
                </p>
                <p className="text-xs text-surface-400">credits</p>
              </div>
            </div>

            {/* Dates */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-500 dark:text-surface-400">Created</span>
                <span className="text-surface-700 dark:text-surface-300">
                  {new Date(referral.createdAt).toLocaleString()}
                </span>
              </div>
              {referral.rewardGrantedDate && (
                <div className="flex justify-between">
                  <span className="text-surface-500 dark:text-surface-400">Reward Granted</span>
                  <span className="text-surface-700 dark:text-surface-300">
                    {new Date(referral.rewardGrantedDate).toLocaleString()}
                  </span>
                </div>
              )}
              {referral.blockReason && (
                <div className="flex justify-between">
                  <span className="text-surface-500 dark:text-surface-400">Block Reason</span>
                  <span className="text-red-600 dark:text-red-400">{referral.blockReason}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function BlockReferralModal({ referral, isOpen, onClose, onBlock }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleBlock = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason for blocking');
      return;
    }

    setLoading(true);
    try {
      await adminApi.blockReferral(referral._id, reason);
      toast.success('Referral blocked successfully');
      onBlock();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to block referral');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white dark:bg-surface-800 rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6 border-b border-surface-200 dark:border-surface-700">
            <Dialog.Title className="text-xl font-semibold text-surface-900 dark:text-white flex items-center gap-2">
              <NoSymbolIcon className="w-6 h-6 text-red-500" />
              Block Referral
            </Dialog.Title>
          </div>
          
          <div className="p-6 space-y-4">
            <p className="text-surface-600 dark:text-surface-400">
              Are you sure you want to block this referral? This will prevent any rewards from being granted.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Reason for blocking
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={3}
                placeholder="e.g., Suspected fraud, self-referral attempt..."
              />
            </div>
          </div>
          
          <div className="p-6 border-t border-surface-200 dark:border-surface-700 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 text-surface-700 dark:text-surface-300 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBlock}
              disabled={loading || !reason.trim()}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Blocking...' : 'Block Referral'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

export default function Referrals() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);

  useEffect(() => {
    loadData();
  }, [statusFilter, searchQuery]);

  const loadData = async (page = 1) => {
    setLoading(true);
    try {
      const [statsRes, referralsRes] = await Promise.all([
        adminApi.getReferralStats(),
        adminApi.getReferrals({
          page,
          limit: 20,
          status: statusFilter || undefined,
          search: searchQuery || undefined
        })
      ]);

      setStats(statsRes.data.stats);
      setReferrals(referralsRes.data.referrals || []);
      setPagination({
        page: referralsRes.data.page,
        pages: referralsRes.data.pages,
        total: referralsRes.data.total
      });
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (referral) => {
    try {
      const response = await adminApi.getReferralDetails(referral._id);
      setSelectedReferral(response.data.referral);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load referral details');
    }
  };

  const handleBlockClick = (referral) => {
    setSelectedReferral(referral);
    setShowBlockModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900 dark:text-white flex items-center gap-3">
            <GiftIcon className="w-8 h-8 text-primary-500" />
            Referral Management
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mt-1">
            Monitor and manage the refer-a-friend program
          </p>
        </div>
        <button
          onClick={() => loadData()}
          className="p-2 text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            icon={UserGroupIcon}
            label="Total Referrals"
            value={stats.totalReferrals}
            color="blue"
          />
          <StatCard
            icon={ClockIcon}
            label="Pending"
            value={stats.pendingReferrals}
            color="amber"
          />
          <StatCard
            icon={CheckCircleIcon}
            label="Completed"
            value={stats.completedReferrals}
            color="emerald"
          />
          <StatCard
            icon={XCircleIcon}
            label="Blocked"
            value={stats.blockedReferrals}
            color="red"
          />
          <StatCard
            icon={SparklesIcon}
            label="Credits Distributed"
            value={stats.totalCreditsDistributed}
            color="purple"
            subtext={`${stats.conversionRate}% conversion`}
          />
        </div>
      )}

      {/* Top Referrers */}
      {stats?.topReferrers && stats.topReferrers.length > 0 && (
        <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-5">
          <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-4 flex items-center gap-2">
            <ChartBarIcon className="w-5 h-5 text-primary-500" />
            Top Referrers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {stats.topReferrers.slice(0, 5).map((referrer, index) => (
              <div key={referrer.companyId} className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    index === 0 ? 'bg-yellow-100 text-yellow-600' :
                    index === 1 ? 'bg-gray-100 text-gray-600' :
                    index === 2 ? 'bg-amber-100 text-amber-700' :
                    'bg-surface-200 text-surface-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-surface-900 dark:text-white truncate">
                      {referrer.companyName}
                    </p>
                    <p className="text-sm text-surface-500 dark:text-surface-400">
                      {referrer.referralCount} referrals
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              placeholder="Search by company name or referral code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-surface-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-surface-50 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {/* Referrals Table */}
      <div className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : referrals.length === 0 ? (
          <div className="p-12 text-center">
            <GiftIcon className="w-12 h-12 text-surface-300 dark:text-surface-600 mx-auto mb-3" />
            <p className="text-surface-600 dark:text-surface-400">No referrals found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-50 dark:bg-surface-900/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Referrer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Referred
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-500 dark:text-surface-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                  {referrals.map((referral) => (
                    <tr key={referral._id} className="hover:bg-surface-50 dark:hover:bg-surface-900/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-surface-900 dark:text-white">
                          {referral.referrerCompanyId?.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-surface-500 dark:text-surface-400">
                          {referral.referrerCompanyId?.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-surface-900 dark:text-white">
                          {referral.referredCompanyId?.name || 'Unknown'}
                        </div>
                        <div className="text-sm text-surface-500 dark:text-surface-400">
                          {referral.referredCompanyId?.slug}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm font-mono text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded">
                          {referral.referralCode}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          referral.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                            : referral.status === 'blocked'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
                        }`}>
                          {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-surface-500 dark:text-surface-400">
                        {new Date(referral.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(referral)}
                            className="p-1.5 text-surface-500 hover:text-primary-600 dark:hover:text-primary-400 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="w-5 h-5" />
                          </button>
                          {referral.status === 'pending' && (
                            <button
                              onClick={() => handleBlockClick(referral)}
                              className="p-1.5 text-surface-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                              title="Block Referral"
                            >
                              <NoSymbolIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-surface-200 dark:border-surface-700 flex items-center justify-between">
                <p className="text-sm text-surface-600 dark:text-surface-400">
                  Showing page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadData(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="px-3 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 rounded-lg disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => loadData(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="px-3 py-1 bg-surface-100 hover:bg-surface-200 dark:bg-surface-700 dark:hover:bg-surface-600 rounded-lg disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ReferralDetailModal
        referral={selectedReferral}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedReferral(null);
        }}
      />

      <BlockReferralModal
        referral={selectedReferral}
        isOpen={showBlockModal}
        onClose={() => {
          setShowBlockModal(false);
          setSelectedReferral(null);
        }}
        onBlock={() => loadData(pagination.page)}
      />
    </div>
  );
}
